import React from 'react';
import { Trash2 } from 'lucide-react';

/**
 * AttributeRow — shared editor for one TestAttribute.
 *
 * Spec: specs/30-modules/skeptics-frontend/test-step-generation.spec.md
 * Used by both wizard (TestStepGeneration.js) and dashboard (ViewControl.js).
 *
 * Props:
 *   attr:       TestAttribute object
 *   isEditing:  bool — when false, all inputs are disabled
 *   onChange:   (field, value) => void
 *   onDelete:   () => void
 */
const DATA_TYPES = ['text', 'date', 'amount', 'person', 'boolean', 'enum'];

export default function AttributeRow({ attr, isEditing, onChange, onDelete }) {
    const showExpected = attr.data_type !== 'person' && attr.data_type !== 'text';
    const isEnum = attr.data_type === 'enum';

    return (
        <div className="rounded border border-slate-200 p-2 bg-slate-50">
            <input
                type="text"
                value={attr.label || ''}
                onChange={(e) => onChange('label', e.target.value)}
                disabled={!isEditing}
                className="w-full text-sm font-semibold border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-1 py-0.5"
                placeholder="Short title (e.g. 'Pricing Specified')"
            />
            <textarea
                value={attr.description || ''}
                onChange={(e) => onChange('description', e.target.value)}
                disabled={!isEditing}
                rows={2}
                className="w-full text-xs text-slate-600 resize-none border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-1 py-0.5 mt-1"
                placeholder="What is being checked (e.g. 'Contract specifies a billing frequency')"
            />
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-700">
                <label className="flex items-center gap-1">
                    Type:
                    <select
                        value={attr.data_type || 'text'}
                        onChange={(e) => onChange('data_type', e.target.value)}
                        disabled={!isEditing}
                        className="border border-slate-300 rounded px-1 py-0.5 bg-white"
                    >
                        {DATA_TYPES.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </label>
                <label className="flex items-center gap-1">
                    <input
                        type="checkbox"
                        checked={attr.required ?? true}
                        onChange={(e) => onChange('required', e.target.checked)}
                        disabled={!isEditing}
                    />
                    Required
                </label>
                {showExpected && (
                    <label className="flex items-center gap-1">
                        Expected:
                        <input
                            type="text"
                            value={attr.expected_value || ''}
                            onChange={(e) => onChange('expected_value', e.target.value || null)}
                            disabled={!isEditing}
                            placeholder={attr.data_type === 'boolean' ? 'true' : '...'}
                            className="border border-slate-300 rounded px-1 py-0.5 w-24 bg-white"
                        />
                    </label>
                )}
                {isEnum && (
                    <label className="flex items-center gap-1">
                        Values:
                        <input
                            type="text"
                            value={(attr.enum_values || []).join(', ')}
                            onChange={(e) => onChange(
                                'enum_values',
                                e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                            )}
                            disabled={!isEditing}
                            placeholder="approved, pending, rejected"
                            className="border border-slate-300 rounded px-1 py-0.5 w-48 bg-white"
                        />
                    </label>
                )}
                {isEditing && (
                    <button
                        type="button"
                        onClick={onDelete}
                        className="ml-auto text-red-500 hover:text-red-700"
                        aria-label="Delete attribute"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
